import { useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { useMatch } from '@tanstack/react-router';
import { CheckIcon, ChevronDownIcon, FlagIcon, TagIcon } from 'lucide-react';
import { type CSSProperties, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zLabel } from 'sdk/zod.gen';
import { labelSlug } from 'shared/config/labels-config';
import { generateId } from 'shared/utils/entity-id';
import { useBreakpointBelow } from '~/hooks/use-breakpoints';
import { useOrganizationLayoutContext } from '~/hooks/use-route-context';
import { ComboboxSearchInput } from '~/modules/common/combobox-search-input';
import { deduplicateLabels } from '~/modules/label/group-labels';
import { useLabelRecencyStore } from '~/modules/label/label-recency-store';
import { type Label, labelsCanonicalOptions, useLabelCreateMutation } from '~/modules/label/query';
import { useLabelUsage } from '~/modules/label/use-label-usage';
import { projectsListQueryOptions } from '~/modules/project/query';
import {
  ComboboxHotkeyHint,
  comboboxActionButtonClass,
  comboboxInputPinClass,
  comboboxScrollClass,
  comboboxShellClass,
  HotkeyIndexBadge,
  matchDigitHotkey,
} from '~/modules/task/dropdowns/combobox-scaffold';
import type { SelectLabelsProps } from '~/modules/task/dropdowns/types';
import { getItemsSortedByName } from '~/modules/task/helpers/sort-helpers';
import { useLiveSelection } from '~/modules/task/hooks/use-live-selection';
import { labelColors } from '~/modules/task/task-styles';
import type { TaskLabel } from '~/modules/task/types';
import { Badge } from '~/modules/ui/badge';
import { Combobox, ComboboxGroup, ComboboxGroupLabel, ComboboxItem, ComboboxList } from '~/modules/ui/combobox';
import { ScrollArea } from '~/modules/ui/scroll-area';
import { createOptimisticEntity } from '~/query/basic/create-optimistic';
import { COALESCED } from '~/query/offline/prepared-mutation';
import { cn } from '~/utils/cn';

// Sentinel prefix for the "create label" row's Combobox value, so it can't
// collide with a real label name when selection flows through onValueChange.
const CREATE_SENTINEL_PREFIX = '__create__';

// The task label picker offers secondary tags and epics; primary labels have dedicated task-card UI.
// Module-scoped so its identity is stable: an inline combine rebuilds `labels` every render,
// cascading through the projectLabels / initLabels memos that depend on it by reference.
const combineTaskLabels = (results: { data?: { items: Label[] } }[]): Label[] =>
  results.flatMap((r) => r.data?.items ?? []).filter((l) => l.mode !== 'primary');

const renderLabelItem = (
  label: Label | TaskLabel,
  selectedLabels: (Label | TaskLabel)[],
  projectId: string,
  searchValue: string,
  hotkeyIndex?: number,
) => {
  const isSelected = selectedLabels.some((l) => l.slug === label.slug);
  const LabelModeIcon = label.mode === 'epic' ? FlagIcon : TagIcon;
  return (
    <ComboboxItem
      key={label.id}
      value={label.slug}
      className={cn('group flex h-9 w-full items-center gap-2 rounded-md pr-2 leading-normal', isSelected && 'font-medium')}
    >
      <LabelModeIcon className="mr-1 ml-0.5 size-3.5 shrink-0 opacity-50" />
      <div className={cn('grow', label.projectId !== projectId && !isSelected && 'opacity-50')}>{label.name}</div>
      <span className="pointer-events-none flex size-4 items-center justify-center">
        {isSelected && <CheckIcon className="pointer-coarse:size-5 size-4 text-success" />}
      </span>
      <HotkeyIndexBadge index={searchValue ? undefined : hotkeyIndex} />
    </ComboboxItem>
  );
};

export function SelectLabels({
  value: currentLabels,
  projectId,
  workspaceId: workspaceIdProp,
  onChange,
  taskId,
  triggerWidth = 320,
  initialSelectedCollapsed,
}: SelectLabelsProps) {
  const { t } = useTranslation();
  const isMobile = useBreakpointBelow('sm');
  const { organizationId, tenantId } = useOrganizationLayoutContext();

  // Auto-detect workspace from route context if not passed as prop
  const routeWorkspaceId = useMatch({
    from: '/_app/$tenantId/$organizationSlug/workspace/$slug',
    shouldThrow: false,
    select: (match) => match.context.workspace?.id,
  });
  const workspaceId = workspaceIdProp ?? routeWorkspaceId;

  // Reactively read the workspace's projects from cache (enabled → subscribes so labels re-scope
  // when the list updates; in a workspace route the board has already fetched it).
  const { data: workspaceProjectIds } = useInfiniteQuery({
    ...projectsListQueryOptions({ workspaceId: workspaceId ?? '' }),
    enabled: !!workspaceId,
    select: (data) => data.pages.flatMap((page) => page.items.map((p) => p.id)),
  });

  // Labels are project-homed: aggregate the per-project canonical home lists client-side. Inside a
  // workspace, span its projects; outside one, just the current project. Falls back to the current
  // project until the workspace project list has loaded.
  const labelProjectIds = useMemo(
    () => (workspaceId && workspaceProjectIds ? workspaceProjectIds : [projectId]),
    [workspaceId, workspaceProjectIds, projectId],
  );

  const labels = useQueries({
    queries: labelProjectIds.map((pid) => labelsCanonicalOptions({ organizationId, tenantId, projectId: pid })),
    combine: combineTaskLabels,
  });
  const usage = useLabelUsage(labelProjectIds);

  const inputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: createLabelMutation } = useLabelCreateMutation(tenantId, organizationId);

  const [selectedLabels, setSelectedLabels] = useLiveSelection(taskId, (t) => t.labels, currentLabels);

  const [searchValue, setSearchValue] = useState('');
  const [selectedCollapsed, setSelectedCollapsed] = useState(initialSelectedCollapsed ?? !isMobile);

  const trackUsage = useLabelRecencyStore((s) => s.trackUsage);
  const getScore = useLabelRecencyStore((s) => s.getScore);

  const projectLabels = useMemo(() => labels.filter((l) => l.projectId === projectId), [labels, projectId]);

  // Derive initLabels directly from query data. Memoized so a keystroke (searchValue) doesn't rebuild
  // it and invalidate the searchResults / suggestedLabels memos that depend on its identity.
  const initLabels = useMemo(() => deduplicateLabels(labels, projectId, organizationId, usage), [labels, projectId, organizationId, usage]);

  // Search results filtered by query
  const searchResults = useMemo(() => {
    if (!searchValue.length) return [];
    const matchedLabels = initLabels.filter(({ name }) => name.includes(searchValue));
    return getItemsSortedByName(matchedLabels);
  }, [searchValue, initLabels]);

  // Top 8 labels by recency → usedCount → name, excluding already-selected
  const suggestedLabels = useMemo(() => {
    const selectedSlugs = new Set(selectedLabels.map((l) => l.slug));
    return initLabels
      .filter((l) => !selectedSlugs.has(l.slug))
      .sort((a, b) => {
        const recency = getScore(organizationId, b.slug) - getScore(organizationId, a.slug);
        if (recency !== 0) return recency;
        const usageDiff = b.usedCount - a.usedCount;
        if (usageDiff !== 0) return usageDiff;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 8);
  }, [initLabels, selectedLabels, getScore, organizationId]);

  // Combined list for rendering (selected first, then suggested)
  const visibleLabels = useMemo(
    () => (searchValue ? searchResults : [...selectedLabels, ...suggestedLabels]),
    [searchValue, searchResults, selectedLabels, suggestedLabels],
  );

  const updateTaskLabels = (updatedLabels: (Label | TaskLabel)[]) => {
    onChange(updatedLabels);
  };

  // For non-create rows `value` is the picked label's slug (the ComboboxItem value); the slug is the
  // cross-project group identity, so a suggested representative resolves to this project's own row.
  const handleSelectClick = async (value?: string | null): Promise<void> => {
    if (!value) return;

    // Create row selected (via click or Enter): strip sentinel and create.
    if (value.startsWith(CREATE_SENTINEL_PREFIX)) {
      return handleCreateClick(value.slice(CREATE_SENTINEL_PREFIX.length));
    }

    setSearchValue('');

    if (inputRef.current && !isMobile) inputRef.current.focus();

    const existingLabel = selectedLabels.find((label) => label.slug === value);
    if (existingLabel) {
      const updatedLabels = selectedLabels.filter((label) => label.id !== existingLabel.id);
      setSelectedLabels(updatedLabels);
      updateTaskLabels(updatedLabels);
      return;
    }

    const newLabel = projectLabels.find((label) => label.slug === value);
    if (newLabel) {
      const updatedLabels = getItemsSortedByName([...selectedLabels, newLabel]);
      setSelectedLabels(updatedLabels);
      updateTaskLabels(updatedLabels);
      trackUsage(organizationId, [newLabel.slug]);
      return;
    }

    handleCreateClick(value);
  };

  const handleCreateClick = async (value: string): Promise<void> => {
    setSearchValue('');

    // Resolve the typed name through its slug to reuse an existing label identity.
    const slug = labelSlug(value);
    const existingLabel = projectLabels.find((label) => label.slug === slug);
    if (existingLabel) return handleSelectClick(existingLabel.slug);

    if (inputRef.current && !isMobile) inputRef.current.focus();

    const matchedLabelColor = labels.find((label) => label.slug === slug)?.color;
    const fallbackColor = labelColors[Math.floor(Math.random() * labelColors.length)];

    const newLabelData = {
      id: generateId(),
      name: value,
      // Ad-hoc created labels are always secondary; primary/epic labels are managed elsewhere.
      mode: 'secondary' as const,
      slug,
      icon: null,
      color: matchedLabelColor ?? fallbackColor,
      keywords: '',
      projectId,
      organizationId: organizationId,
      tenantId,
    };

    // Build full label from schema defaults + creation data (avoids manual field repetition)
    const newLabel = createOptimisticEntity(zLabel, newLabelData);

    // Sort updated labels by name
    const updatedLabels = getItemsSortedByName([...selectedLabels, newLabel]);
    setSelectedLabels(updatedLabels);

    // Create label first, then update task after the server has the referenced label.
    const createdLabels = await createLabelMutation(newLabelData);
    // Creates never coalesce, so this always resolves to the created labels; the guard narrows the type.
    if (createdLabels === COALESCED) return;
    const finalLabels = updatedLabels.map((l) => (l.id === newLabel.id ? createdLabels[0] : l));
    setSelectedLabels(finalLabels);
    updateTaskLabels(finalLabels);
    trackUsage(organizationId, [newLabelData.slug]);
  };

  return (
    <Combobox<string>
      inline
      openOnInputClick={false}
      value={null}
      onValueChange={(value) => handleSelectClick(value)}
      inputValue={searchValue}
      onInputValueChange={(value) => {
        // Digit hotkey: pick suggested label by 1-based index
        const hotkeyLabel = matchDigitHotkey(suggestedLabels, value, { max: 8 });
        if (hotkeyLabel) {
          handleSelectClick(hotkeyLabel.slug);
          return;
        }
        // Replace spaces with dashes only when there's content
        const updated = value.trim() ? value.replaceAll(' ', '-') : value;
        setSearchValue(updated.toLowerCase());
      }}
      filter={() => true}
    >
      <div className={comboboxShellClass} style={{ '--trigger-width': `${triggerWidth}px` } as CSSProperties}>
        <ComboboxSearchInput
          ref={inputRef}
          autoFocus={!isMobile}
          value={searchValue}
          wrapClassName={comboboxInputPinClass}
          className="min-h-10 leading-normal"
          placeholder={
            visibleLabels.length
              ? t('c:select_or_create_resource', { resource: t('c:label').toLowerCase() })
              : projectLabels.length
                ? t('c:create_label.text')
                : t('c:create_first_label.text')
          }
          showClear={false}
        />
        <ComboboxHotkeyHint searching={!!searchValue}>L</ComboboxHotkeyHint>
        {!searchValue && selectedLabels.length > 0 && (
          <button
            type="button"
            aria-expanded={!selectedCollapsed}
            aria-controls="select-labels-selected-group"
            className="mx-1 mt-1 flex w-[calc(100%-0.5rem)] shrink-0 items-center gap-2 pointer-coarse:px-3 px-2 pointer-coarse:py-2 py-1.5 pointer-coarse:text-sm text-muted-foreground text-xs outline-hidden hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
            onClick={() => setSelectedCollapsed(!selectedCollapsed)}
          >
            {t('c:selected')}
            {selectedCollapsed && <span className="ml-1 text-xs opacity-70">{selectedLabels.length}</span>}
            <span className="grow" />
            <ChevronDownIcon className={cn('size-3.5 transition-transform', !selectedCollapsed && 'rotate-180')} />
          </button>
        )}
        <ScrollArea className={comboboxScrollClass}>
          <ComboboxList className="p-1!">
            {searchValue ? (
              searchResults.length > 0 ? (
                <ComboboxGroup>
                  <ComboboxGroupLabel>{t('c:results')}</ComboboxGroupLabel>
                  {searchResults.map((label) => renderLabelItem(label, selectedLabels, projectId, searchValue))}
                </ComboboxGroup>
              ) : (
                <div className="flex items-center justify-center p-1.5 text-center text-muted-foreground/50 text-sm">
                  {t('c:no_resource_found', { resource: t('c:label_other').toLowerCase() })}
                </div>
              )
            ) : (
              <>
                {selectedLabels.length > 0 && !selectedCollapsed && (
                  <ComboboxGroup id="select-labels-selected-group">
                    {selectedLabels.map((label) => renderLabelItem(label, selectedLabels, projectId, searchValue))}
                  </ComboboxGroup>
                )}
                <ComboboxGroup>
                  <ComboboxGroupLabel>{t('c:suggested')}</ComboboxGroupLabel>
                  {suggestedLabels.map((label, index) => renderLabelItem(label, selectedLabels, projectId, searchValue, index))}
                </ComboboxGroup>
              </>
            )}
            {searchValue.trim() !== '' && !projectLabels.some((l) => l.slug === labelSlug(searchValue)) && (
              <ComboboxItem value={`${CREATE_SENTINEL_PREFIX}${searchValue}`} className={cn(comboboxActionButtonClass, 'mt-1')}>
                {t('c:create_resource', { resource: t('c:label').toLowerCase() })}
                <Badge className="ml-2 flex px-2 py-0" variant="plain">
                  {searchValue}
                </Badge>
              </ComboboxItem>
            )}
          </ComboboxList>
          {visibleLabels.length === 0 && searchValue.trim() === '' && (
            <div className="flex items-center justify-center p-1.5 text-muted-foreground/50 text-sm">
              {t('c:no_resource_yet', { resource: t('c:label_other').toLowerCase() })}
            </div>
          )}
        </ScrollArea>
      </div>
    </Combobox>
  );
}
